// Single source of truth for listing availability on the client.
//
// A listing is available on a day when the day is not in the past, lies inside
// the owner's window (available_date .. available_until, both optional and
// inclusive) and is not a blocked day (unavailable_dates). Month-level
// availability is DERIVED from that (a month with any blocked day is out,
// monthly stays need the whole month) and mirrors the SQL trigger
// `derive_available_months`, which feeds every scoring engine.
//
// All dates are date-only strings (yyyy-MM-dd) handled as LOCAL calendar days:
// parseISO/format only, never `new Date("yyyy-MM-dd")`, which parses as UTC
// midnight and shifts a day in UTC-negative locales (Costa Rica is UTC-6).
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachMonthOfInterval,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns";

export interface AvailabilitySource {
  /** Window start (yyyy-MM-dd) or null = open. */
  availableDate: string | null;
  /** Window end, inclusive (yyyy-MM-dd) or null = open-ended. */
  availableUntil?: string | null;
  /** Blocked days (yyyy-MM-dd). */
  unavailableDates: string[];
  /** Minimum stay in months (>= 1). */
  minRentalMonths: number;
}

export type StayIssue = "past" | "before_window" | "after_window" | "min_stay" | "blocked";

export interface StayValidation {
  ok: boolean;
  issues: StayIssue[];
  /** First blocked day inside the stay, when `blocked` is among the issues. */
  firstBlocked?: string;
  /** Checkout day (exclusive end), yyyy-MM-dd. */
  moveOut: string;
}

export const toIso = (d: Date): string => format(d, "yyyy-MM-dd");
export const fromIso = (s: string): Date => startOfDay(parseISO(s));
export const monthKey = (d: Date): string => format(d, "yyyy-MM");

const minMonths = (src: AvailabilitySource) => Math.max(1, Math.floor(src.minRentalMonths || 1));

const blockedSet = (src: AvailabilitySource) => new Set(src.unavailableDates ?? []);

export const isBlocked = (src: AvailabilitySource, day: Date): boolean =>
  blockedSet(src).has(toIso(day));

export const isWithinWindow = (src: AvailabilitySource, day: Date): boolean => {
  if (src.availableDate && isBefore(day, fromIso(src.availableDate))) return false;
  if (src.availableUntil && isAfter(day, fromIso(src.availableUntil))) return false;
  return true;
};

/** Not past, inside the window, not blocked. */
export const isDayAvailable = (
  src: AvailabilitySource,
  day: Date,
  today: Date = startOfDay(new Date()),
): boolean => !isBefore(day, today) && isWithinWindow(src, day) && !isBlocked(src, day);

/** Checkout day for a stay of `months` starting `moveIn` (exclusive end). */
export const stayEnd = (moveIn: Date, months: number): Date => addMonths(moveIn, Math.max(1, months));

/**
 * Mirrors public.derive_available_months: the next `count` months from
 * `from`'s month; a month is available when the window overlaps it and it has
 * no blocked day.
 */
export const deriveAvailableMonths = (
  src: AvailabilitySource,
  from: Date = new Date(),
  count = 18,
): string[] => {
  const first = startOfMonth(from);
  const months = eachMonthOfInterval({ start: first, end: addMonths(first, count - 1) });
  const blocked = src.unavailableDates ?? [];
  const windowFrom = src.availableDate ? fromIso(src.availableDate) : null;
  const windowUntil = src.availableUntil ? fromIso(src.availableUntil) : null;
  return months
    .filter((m) => {
      const mStart = startOfMonth(m);
      const mEnd = endOfMonth(m);
      if (windowFrom && isAfter(windowFrom, mEnd)) return false;
      if (windowUntil && isBefore(windowUntil, mStart)) return false;
      const key = monthKey(m);
      return !blocked.some((iso) => iso.startsWith(key));
    })
    .map(monthKey);
};

/** Every night from `from` (inclusive) to `toExclusive` is available. */
export const isRangeAvailable = (
  src: AvailabilitySource,
  from: Date,
  toExclusive: Date,
  today: Date = startOfDay(new Date()),
): { ok: boolean; firstBlocked?: string } => {
  const nights = differenceInCalendarDays(toExclusive, from);
  if (nights <= 0) return { ok: false };
  const blocked = blockedSet(src);
  for (let i = 0; i < nights; i++) {
    const day = addDays(from, i);
    if (isBefore(day, today) || !isWithinWindow(src, day)) return { ok: false };
    const iso = toIso(day);
    if (blocked.has(iso)) return { ok: false, firstBlocked: iso };
  }
  return { ok: true };
};

/**
 * Can a renter start a stay of `months` on `moveIn`? Returns every reason it
 * can't, so UIs can explain instead of just disabling.
 */
export const validateStay = (
  src: AvailabilitySource,
  moveIn: Date,
  months: number,
  today: Date = startOfDay(new Date()),
): StayValidation => {
  const issues: StayIssue[] = [];
  const start = startOfDay(moveIn);
  const end = stayEnd(start, months);
  const lastNight = addDays(end, -1);
  if (isBefore(start, today)) issues.push("past");
  if (months < minMonths(src)) issues.push("min_stay");
  if (src.availableDate && isBefore(start, fromIso(src.availableDate))) issues.push("before_window");
  if (src.availableUntil && isAfter(lastNight, fromIso(src.availableUntil))) issues.push("after_window");
  let firstBlocked: string | undefined;
  const blocked = blockedSet(src);
  const nights = differenceInCalendarDays(end, start);
  for (let i = 0; i < nights; i++) {
    const iso = toIso(addDays(start, i));
    if (blocked.has(iso)) {
      firstBlocked = iso;
      issues.push("blocked");
      break;
    }
  }
  return { ok: issues.length === 0, issues, firstBlocked, moveOut: toIso(end) };
};

/**
 * First day from which a minimum-length stay fits (Furnished-Finder style
 * "Available from"), scanning up to `horizonDays` ahead. Null when nothing
 * fits, e.g. the window has ended or every month has a block.
 */
export const computeAvailableFrom = (
  src: AvailabilitySource,
  today: Date = startOfDay(new Date()),
  horizonDays = 548,
): Date | null => {
  const months = minMonths(src);
  let cursor = today;
  if (src.availableDate) {
    const from = fromIso(src.availableDate);
    if (isAfter(from, cursor)) cursor = from;
  }
  const limit = addDays(today, horizonDays);
  const blocked = blockedSet(src);
  while (!isAfter(cursor, limit)) {
    // Jump past a blocked start day directly; otherwise validate the run.
    if (blocked.has(toIso(cursor))) {
      cursor = addDays(cursor, 1);
      continue;
    }
    const v = validateStay(src, cursor, months, today);
    if (v.ok) return cursor;
    if (v.issues.includes("after_window")) return null;
    if (v.firstBlocked) {
      cursor = addDays(fromIso(v.firstBlocked), 1);
      continue;
    }
    cursor = addDays(cursor, 1);
  }
  return null;
};

export type AvailabilityFlag = "window_ended" | "no_availability_90d" | "next_month_blocked" | "no_window_start";

export interface AvailabilitySummary {
  availableFrom: Date | null;
  /** Available months among the next 12 (derived policy). */
  monthsAvailableNext12: number;
  /** Blocked days from today on. */
  blockedFutureDays: number;
  minMonths: number;
  flags: AvailabilityFlag[];
}

/** One-line facts for owner dashboards and admin review. */
export const summarizeAvailability = (
  src: AvailabilitySource,
  today: Date = startOfDay(new Date()),
): AvailabilitySummary => {
  const availableFrom = computeAvailableFrom(src, today);
  const months12 = deriveAvailableMonths(src, today, 12);
  const blockedFutureDays = (src.unavailableDates ?? []).filter((iso) => !isBefore(fromIso(iso), today)).length;
  const flags: AvailabilityFlag[] = [];
  if (src.availableUntil && isBefore(fromIso(src.availableUntil), today)) flags.push("window_ended");
  if (!availableFrom || isAfter(availableFrom, addDays(today, 90))) flags.push("no_availability_90d");
  const next = monthKey(addMonths(today, 1));
  if (!months12.includes(next) && !flags.includes("window_ended")) flags.push("next_month_blocked");
  return { availableFrom, monthsAvailableNext12: months12.length, blockedFutureDays, minMonths: minMonths(src), flags };
};

export interface AvailabilityPill {
  label: string;
  tone: "info" | "warn";
}

/**
 * The one-line availability label an owner sees on their dashboard card:
 * "Available now", "Available from Oct 1, 2026", optionally "· until Mar 31,
 * 2027", or "No availability soon" when no minimum-length stay fits. Derived
 * from the same window, blocked days and minimum stay the matching engines
 * use, so an availability edit is reflected the moment the card reloads.
 */
export const availabilityPill = (src: AvailabilitySource, today: Date = startOfDay(new Date())): AvailabilityPill => {
  const from = computeAvailableFrom(src, today);
  if (!from) return { label: "No availability soon", tone: "warn" };
  const fmt = (d: Date) => format(d, "MMM d, yyyy");
  const start = isAfter(from, today) ? `Available from ${fmt(from)}` : "Available now";
  const until = src.availableUntil ? ` · until ${fmt(fromIso(src.availableUntil))}` : "";
  return { label: `${start}${until}`, tone: "info" };
};

/** "Nov 15 → May 15 · 6 months" */
export const formatStay = (moveInIso: string, moveOutIso: string): string => {
  const a = fromIso(moveInIso);
  const b = fromIso(moveOutIso);
  const sameYear = a.getFullYear() === b.getFullYear();
  const months = Math.max(1, Math.round(differenceInCalendarDays(b, a) / 30.4));
  return `${format(a, sameYear ? "MMM d" : "MMM d, yyyy")} → ${format(b, "MMM d, yyyy")} · ${months} month${months === 1 ? "" : "s"}`;
};
